import { useEffect, useMemo, useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTimeout } from "@/hooks/use-timers";
import { cn } from "@/lib/utils";

import {
  HeartIconActive,
  HeartIconInactive,
  ThumbsDownActiveIcon,
  ThumbsDownInactiveIcon,
  ThumbsUpActiveIcon,
  ThumbsUpInactiveIcon,
} from "./icons";

export const OverallRatingEnum = {
  DISLIKE: "DISLIKE",
  LIKE: "LIKE",
  LOVE: "LOVE",
} as const;

const PSEUDO_CLASS_NAME = cn(
  'after:absolute after:inset-y-0 after:block after:w-[29px] after:border-3 after:content-[""]',
  'before:absolute before:inset-y-0 before:block before:w-[29px] before:border-3 before:content-[""]'
);

const TOOLTIP_ARROW_CLASS_NAME = cn("bg-dark-purple fill-dark-purple");

const TOOLTIP_CLASS_NAME = cn(
  "flex h-6.5 items-center rounded-[13px] bg-dark-purple px-2.5 py-0 font-medium text-13 text-white"
);

const LABEL_TEXT_CLASS_NAMES: Record<keyof typeof OverallRatingEnum, string> = {
  [OverallRatingEnum.DISLIKE]: "text-left",
  [OverallRatingEnum.LIKE]: "text-center pl-1.5",
  [OverallRatingEnum.LOVE]: "text-right pr-1.5",
};

const RATING_LABELS = {
  [OverallRatingEnum.DISLIKE]: "Disliked",
  [OverallRatingEnum.LIKE]: "Liked",
  [OverallRatingEnum.LOVE]: "Loved",
};

interface RateOptionsProps {
  expanded?: boolean;
  onClick?(rating: keyof typeof OverallRatingEnum): void;
  onRating?(rating: keyof typeof OverallRatingEnum): void;
  overallRating?: keyof typeof OverallRatingEnum;
  withLabels?: boolean;
}

export default function RateOptions({
  expanded = true,
  onClick = () => {
    /* No-op */
  },
  onRating = () => {
    /* No-op */
  },
  overallRating,
  withLabels = false,
}: RateOptionsProps) {
  const [ratingHover, setRatingHover] = useState(expanded || withLabels);
  const [labelVisible, setLabelVisible] = useState(false);

  const [isDislikeTooltipOpen, setIsDislikeTooltipOpen] = useState(
    withLabels && labelVisible && overallRating === OverallRatingEnum.DISLIKE
  );
  const [isLikeTooltipOpen, setIsLikeTooltipOpen] = useState(
    withLabels && labelVisible && overallRating === OverallRatingEnum.LIKE
  );
  const [isLoveTooltipOpen, setIsLoveTooltipOpen] = useState(
    withLabels && labelVisible && overallRating === OverallRatingEnum.LOVE
  );

  const { start } = useTimeout(() => setLabelVisible(false), 3000);
  // const [maize100, torchRed100] = useToken('colors', ['maize.100', 'torchRed.100']);

  const handleRating = (rating: keyof typeof OverallRatingEnum) => () => {
    if (ratingHover || rating !== overallRating) {
      onClick(rating);
    }
    // don't update the rating if it's the same as the current rating
    if (rating !== overallRating) {
      onRating(rating);
    }

    setLabelVisible(true);
    start();
  };

  const handleRatingClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setRatingHover(true);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: close the rating options when the overall rating changes
  useEffect(() => {
    setRatingHover(expanded || withLabels);
  }, [overallRating, withLabels, expanded]);

  const { disliked, fromColor, initial, liked, loved, toColor } =
    useMemo(() => {
      const initial = !overallRating;
      const liked = overallRating === OverallRatingEnum.LIKE;
      const loved = overallRating === OverallRatingEnum.LOVE;
      const disliked = overallRating === OverallRatingEnum.DISLIKE;

      let fromColor = "--color-goldenrod";
      if (!ratingHover && loved) {
        fromColor = "--color-crimson-bright";
      }

      let toColor = "--color-crimson-bright";
      if (!ratingHover && liked) {
        toColor = "--color-goldenrod";
      }

      return {
        disliked,
        fromColor,
        initial,
        liked,
        loved,
        toColor,
      };
    }, [overallRating, ratingHover]);

  return (
    <div className="relative">
      <div className="animate-in">
        {/** biome-ignore lint/a11y/noStaticElementInteractions: its fine */}
        {/** biome-ignore lint/a11y/useKeyWithClickEvents: its fine */}
        {/** biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy */}
        <div
          className="flex items-center justify-center gap-2"
          onClick={handleRatingClick}
          onMouseEnter={() => setRatingHover(true)}
          onMouseLeave={() => setRatingHover(expanded || withLabels)}
        >
          {(ratingHover || initial || disliked) && (
            <div className="fade-in-0 animate-in">
              <Tooltip
                onOpenChange={setIsDislikeTooltipOpen}
                open={isDislikeTooltipOpen}
              >
                <TooltipTrigger
                  aria-label="Dislike"
                  className="size-11.5 rounded-full border-3 border-sky-blue shadow-0-0-8 shadow-sky-blue"
                  onClick={handleRating(OverallRatingEnum.DISLIKE)}
                >
                  <AnimatedThumbsDownIcon active={disliked} init={initial} />
                </TooltipTrigger>
                <TooltipContent
                  arrowClassName={TOOLTIP_ARROW_CLASS_NAME}
                  className={TOOLTIP_CLASS_NAME}
                >
                  {RATING_LABELS[OverallRatingEnum.DISLIKE]}
                </TooltipContent>
              </Tooltip>
            </div>
          )}
          {/* right side thumbsup and heart icons */}
          {(ratingHover || initial || !disliked) &&
            (() => {
              let widthClass = "w-11.5";
              if (ratingHover || initial) {
                widthClass = "w-23";
              } else if (disliked) {
                widthClass = "w-0";
              }

              return (
                <div
                  className={cn(
                    "relative flex h-11.5 animate-in justify-between overflow-hidden rounded-[23px] bg-[linear-gradient(90deg,var(--from-color),var(--to-color)),linear-gradient(90deg,var(--from-color),var(--to-color))] bg-position-[23px_0,23px_100%] bg-size-[calc(100%-46px)_3px] bg-no-repeat p-[3px] shadow-0-0-8 shadow-orange-red transition-[colors,width]",
                    "after:right-0 after:rounded-r-[23px] after:border-(--to-color) after:border-l-transparent",
                    "before:left-0 before:rounded-l-[23px] before:border-(--from-color) before:border-r-transparent",
                    widthClass,
                    PSEUDO_CLASS_NAME
                  )}
                  style={
                    {
                      "--from-color": `var(${fromColor})`,
                      "--to-color": `var(${toColor})`,
                    } as React.CSSProperties
                  }
                >
                  {(ratingHover || liked || initial) && (
                    <div className="animate-in">
                      <Tooltip
                        onOpenChange={setIsLikeTooltipOpen}
                        open={isLikeTooltipOpen}
                      >
                        <TooltipTrigger
                          aria-label="Like"
                          className="relative z-1 h-full"
                          onClick={handleRating(OverallRatingEnum.LIKE)}
                        >
                          <AnimatedThumbsUpIcon active={liked} init={initial} />
                        </TooltipTrigger>
                        <TooltipContent
                          arrowClassName={TOOLTIP_ARROW_CLASS_NAME}
                          className={TOOLTIP_CLASS_NAME}
                        >
                          {RATING_LABELS[OverallRatingEnum.LIKE]}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                  {(ratingHover || loved || initial) && (
                    <div className="animate-in">
                      <Tooltip
                        onOpenChange={setIsLoveTooltipOpen}
                        open={isLoveTooltipOpen}
                      >
                        <TooltipTrigger
                          aria-label="Love"
                          className="relative z-1 h-full"
                          onClick={handleRating(OverallRatingEnum.LOVE)}
                        >
                          <AnimatedHeartIcon active={loved} init={initial} />
                        </TooltipTrigger>
                        <TooltipContent
                          arrowClassName={TOOLTIP_ARROW_CLASS_NAME}
                          className={TOOLTIP_CLASS_NAME}
                        >
                          {RATING_LABELS[OverallRatingEnum.LOVE]}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                </div>
              );
            })()}
        </div>
      </div>
      {withLabels && !!overallRating && (
        <p
          className={cn(
            "absolute top-1/1 left-0 w-full pt-2 font-medium text-12/[1.5em] text-white md:hidden",
            LABEL_TEXT_CLASS_NAMES[overallRating]
          )}
        >
          {RATING_LABELS[overallRating]}
        </p>
      )}
    </div>
  );
}

interface RatingIconProps {
  active: boolean;
  init: boolean;
}

function AnimatedThumbsDownIcon({ active, init }: RatingIconProps) {
  return (
    <div
      className={cn(
        "mx-1 flex size-[33px] items-center justify-center rounded-full transition-colors",
        active && "bg-sky-blue"
      )}
    >
      {active ? (
        <ThumbsDownActiveIcon />
      ) : (
        <ThumbsDownInactiveIcon init={init} />
      )}
    </div>
  );
}

function AnimatedThumbsUpIcon({ active, init }: RatingIconProps) {
  return (
    <div
      className={cn(
        "mx-1 flex size-[33px] items-center justify-center rounded-full transition-colors",
        active && "bg-sandy-brown"
      )}
    >
      {active ? <ThumbsUpActiveIcon /> : <ThumbsUpInactiveIcon init={init} />}
    </div>
  );
}

function AnimatedHeartIcon({ active, init }: RatingIconProps) {
  return (
    <div
      className={cn(
        "mx-1 flex size-[33px] items-center justify-center rounded-full transition-colors",
        active && "bg-fire-engine-red"
      )}
    >
      {active ? <HeartIconActive /> : <HeartIconInactive init={init} />}
    </div>
  );
}
